import React, { useRef, useState, useCallback, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DraggableCanvas from '../DraggableCanvas';
import DraggableWindow from '../DraggableWindow';
import FullscreenWindow from '../FullscreenWindow';
import TabbedInterface from './TabbedInterface';
import MenuBar from './MenuBar';
import { useWindowManager } from './hooks/useWindowManager';
import { useAppLoader } from './hooks/useAppLoader';
import useDeepLinking from './hooks/useDeepLinking';
import useRedirectHandler from '../../hooks/useRedirectHandler';
import Navigator from './Navigator';
import AdminMenu from '../Admin/AdminMenu';
import { SDKAppWrapper } from '../StreamingFeatureHost/SDKAppWrapper';
import MicroApiHost from '../StreamingFeatureHost/MicroApiHost';
import { getBundleUrl } from '../Admin/SDKMenu/config';
import { getLayoutPositions } from '../../utils/layoutManager';

const MAX_SPAWNED_WINDOWS_PER_PARENT = 5;
const MAX_TOTAL_SPAWNED_WINDOWS = 15;

/**
 * IframePreview - Renders a preview URL in an iframe
 * Used by openPreview to show service previews in DraggableWindows
 */
function IframePreview({ metadata }) {
  const { url } = metadata || {};
  if (!url) return <div style={{ padding: 16 }}>No preview URL provided</div>;
  return (
    <iframe
      src={url}
      style={{ width: '100%', height: '100%', border: 'none' }}
      title="Preview"
      allow="clipboard-read; clipboard-write"
      sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-pointer-lock"
    />
  );
}

function AppLauncher() {
  const navigate = useNavigate();
  const canvasRef = useRef(null);
  const [showGallery, setShowGallery] = useState(true);
  const [canvasTransform, setCanvasTransform] = useState({
    scale: 1,
    translateX: 0,
    translateY: 0
  });
  const [modifiedApps, setModifiedApps] = useState(new Map());
  const [selectedAppIds, setSelectedAppIds] = useState([]);
  const [viewportWidth, setViewportWidth] = useState(0);
  const [viewportHeight, setViewportHeight] = useState(0);
  // Track parent-child window relationships: parentWindowId -> [childWindowId, ...]
  const [spawnedWindows, setSpawnedWindows] = useState(new Map());

  const { apps, loading, error } = useAppLoader();
  const {
    activeApps,
    windowStates,
    focusedWindowId,
    launchApp: wmLaunchApp,
    closeWindow,
    toggleMinimize,
    updateWindowPosition,
    updateWindowSize,
    bringWindowToFront
  } = useWindowManager(canvasRef);

  // Create a ref to store updateUrl so launchApp can use it (circular dependency workaround)
  const updateUrlRef = useRef(null);
  // Track if we just launched an app to prevent auto-popup gallery race condition
  const justLaunchedAppRef = useRef(false);

  const getAppWithVersion = useCallback((app) => {
    const modified = modifiedApps.get(app.id);
    if (modified) {
      // Spread modified first, then override specific properties
      // This ensures all properties from modified are preserved
      return {
        ...modified,
        versions: app.versions,
        component: modified.component || app.versions?.[modified.selectedVersion]?.component,
        metadata: {
          ...app.metadata,
          ...app.versions?.[modified.selectedVersion]?.metadata,
          ...modified.metadata
        }
        // Note: display_mode and type are already in ...modified spread
      };
    }
    return app;
  }, [modifiedApps]);

  const handleViewportChange = useCallback(({ x, y, scale }) => {
    setCanvasTransform(prev => {
      if (prev.scale === scale && prev.translateX === x && prev.translateY === y) {
        return prev;
      }
      return { translateX: x, translateY: y, scale: scale };
    });
  }, []);

  const launchApp = useCallback(async (app) => {
    const appToLaunch = getAppWithVersion(app);
    if (!appToLaunch.component && appToLaunch.versions) {
      const versionData = appToLaunch.versions[appToLaunch.selectedVersion];
      appToLaunch.component = versionData?.component;
    }

    // Force fullscreen mode on mobile devices
    const isMobile = window.innerWidth < 768;
    if (isMobile) {
      appToLaunch.display_mode = 'fullscreen';
    }

    const newWindowInfo = wmLaunchApp(appToLaunch);
    
    if (newWindowInfo && !isMobile) {
      const { x, y, width, height } = newWindowInfo;
      const scale = canvasTransform.scale;
      const windowCenterX = x + width / 2;
      const windowCenterY = y + height / 2;

      const newCameraX = windowCenterX - (viewportWidth / (2 * scale));
      const newCameraY = windowCenterY - (viewportHeight / (2 * scale));

      handleViewportChange({
        x: newCameraX,
        y: newCameraY,
        scale
      });
    }

    const targetUrl = `/plugins/${appToLaunch.id}`;
    if (window.location.pathname !== targetUrl && updateUrlRef.current) {
      updateUrlRef.current(appToLaunch.id);
    }
    // Hide gallery when launching any app
    justLaunchedAppRef.current = true;
    setShowGallery(false);
  }, [getAppWithVersion, wmLaunchApp, canvasTransform, viewportWidth, viewportHeight, handleViewportChange, setShowGallery]);

  /**
   * Launch SDK as an app in the window system
   */
  const launchSDK = useCallback((variant) => {
    const bundleUrl = getBundleUrl(variant);
    const sdkApp = {
      id: `sdk-${variant.id}`,
      name: variant.name,
      type: 'internal',
      display_mode: 'windowed',
      component: SDKAppWrapper,
      metadata: {
        bundleUrl: bundleUrl,
        title: variant.name,
        width: 1400,
        height: 900
      }
    };
    launchApp(sdkApp);
  }, [launchApp]);

  // Calculate spawn position relative to parent window
  const calculateSpawnPosition = useCallback((parentWindowId, childIndex) => {
    const parentState = windowStates[parentWindowId];
    if (!parentState) {
      return { x: 200 + childIndex * 30, y: 200 + childIndex * 30 };
    }
    const parentPos = parentState.position || { x: 100, y: 100 };
    const parentSize = parentState.size || { width: 800, height: 600 };
    return {
      x: parentPos.x + parentSize.width + 20,
      y: parentPos.y + childIndex * 40
    };
  }, [windowStates]);

  // Get child windows for a given parent
  const getChildWindows = useCallback((parentId) => {
    return spawnedWindows.get(parentId) || [];
  }, [spawnedWindows]);

  // Spawn a child window on the canvas
  const spawnChildWindow = useCallback((config) => {
    const {
      title = 'Spawned Window',
      width = 600,
      height = 400,
      position,
      metadata = {},
      parentWindowId
    } = config;

    if (!parentWindowId) {
      return { error: 'MISSING_PARENT_ID' };
    }

    // Check per-parent limit
    const currentChildren = spawnedWindows.get(parentWindowId) || [];
    if (currentChildren.length >= MAX_SPAWNED_WINDOWS_PER_PARENT) {
      return { error: 'MAX_CHILDREN_REACHED', limit: MAX_SPAWNED_WINDOWS_PER_PARENT };
    }

    // Check total limit
    const totalSpawned = Array.from(spawnedWindows.values()).reduce((sum, arr) => sum + arr.length, 0);
    if (totalSpawned >= MAX_TOTAL_SPAWNED_WINDOWS) {
      return { error: 'MAX_TOTAL_REACHED', limit: MAX_TOTAL_SPAWNED_WINDOWS };
    }

    const windowId = `spawned-${parentWindowId}-${Date.now()}`;
    const spawnPos = position || calculateSpawnPosition(parentWindowId, currentChildren.length);

    const spawnedApp = {
      id: windowId,
      name: title,
      type: 'spawned',
      display_mode: 'windowed',
      component: MicroApiHost,
      metadata: {
        ...metadata,
        parentWindowId,
        width,
        height,
        isSpawned: true
      }
    };

    wmLaunchApp(spawnedApp);

    // Track parent-child relationship
    setSpawnedWindows(prev => {
      const updated = new Map(prev);
      const children = updated.get(parentWindowId) || [];
      updated.set(parentWindowId, [...children, windowId]);
      return updated;
    });

    const channelId = `${parentWindowId}:${windowId}`;

    return {
      windowId,
      position: spawnPos,
      size: { width, height },
      channelId
    };
  }, [wmLaunchApp, spawnedWindows, calculateSpawnPosition]);

  const { updateUrl, activeTab } = useDeepLinking({
    apps,
    launchApp,
    closeWindow,
    activeApps
  });
  // Store updateUrl in ref so launchApp can use it (circular dependency workaround)
  useEffect(() => {
    updateUrlRef.current = updateUrl;
  }, [updateUrl]);

  // Handle URL redirects (e.g., /contact -> ContactForm modal, /about -> PageViewer)
  const { renderModal, renderPageViewer } = useRedirectHandler({
    launchApp,
    apps
  });

  const closeWindowAndUpdateUrl = useCallback((windowId) => {
    // Close all child windows first (cascade close)
    const children = spawnedWindows.get(windowId) || [];
    children.forEach(childId => {
      closeWindow(childId);
    });

    // Clean up parent-child tracking
    if (children.length > 0 || Array.from(spawnedWindows.values()).some(arr => arr.includes(windowId))) {
      setSpawnedWindows(prev => {
        const updated = new Map(prev);
        // Remove this window's children list
        updated.delete(windowId);
        // Remove this window from any parent's children list
        for (const [parentId, childIds] of updated.entries()) {
          const filtered = childIds.filter(id => id !== windowId);
          if (filtered.length === 0) {
            updated.delete(parentId);
          } else {
            updated.set(parentId, filtered);
          }
        }
        return updated;
      });
    }

    closeWindow(windowId);
    const closedIds = [windowId, ...children];
    const remainingApps = activeApps.filter(a => !closedIds.includes(a.id));
    if (remainingApps.length === 0 && window.location.pathname !== '/') {
      updateUrl(null);
    }
  }, [closeWindow, activeApps, updateUrl, spawnedWindows]);

  // Auto-popup gallery when no windows are open
  // Only show gallery if there are truly no apps active
  useEffect(() => {
    // If there are any active apps, don't auto-show gallery
    // (they might still be initializing their windowStates)
    if (activeApps.length > 0) {
      return;
    }
    
    // Only show gallery if there are no apps at all
    if (!showGallery) {
      setShowGallery(true);
    }
  }, [activeApps, showGallery]);

  const handleVersionSwitch = useCallback((appId, version) => {
    const app = apps.find(a => a.id === appId);
    if (!app) return;

    const versionData = app.versions?.[version];
    if (!versionData) return;

    const isActive = activeApps.some(a => a.id === appId);
    if (isActive) closeWindowAndUpdateUrl(appId);

    setModifiedApps(current => {
      const updated = new Map(current);
      const newApp = {
        ...app,
        selectedVersion: version,
        component: versionData.component,
        metadata: { ...app.metadata, ...versionData.metadata },
        versions: app.versions
      };
      updated.set(appId, newApp);

      if (isActive) {
        launchApp(newApp);
      }
      return updated;
    });
  }, [apps, activeApps, closeWindowAndUpdateUrl, launchApp]);

  const applyLayoutToSelectedApps = useCallback((layoutName, appIds) => {
    if (!appIds || appIds.length === 0) return;
    const scale = canvasTransform.scale;
    const { translateX, translateY } = canvasTransform;

    const positions = getLayoutPositions(
      layoutName,
      appIds.length,
      viewportWidth,
      viewportHeight,
      translateX,
      translateY,
      scale
    );

    positions.forEach((pos, i) => {
      const appId = appIds[i];
      updateWindowPosition(appId, { x: pos.x, y: pos.y });
      updateWindowSize(appId, { width: pos.width, height: pos.height });
    });
  }, [canvasTransform, viewportWidth, viewportHeight, updateWindowPosition, updateWindowSize]);

  const snapViewportToApp = useCallback((appId) => {
    const state = windowStates[appId];
    if (!state) return;

    const { x, y } = state.position;
    const { width, height } = state.size;
    const scale = canvasTransform.scale;
    const windowCenterX = x + width / 2;
    const windowCenterY = y + height / 2;

    const newCameraX = windowCenterX - (viewportWidth / (2 * scale));
    const newCameraY = windowCenterY - (viewportHeight / (2 * scale));

    handleViewportChange({ x: newCameraX, y: newCameraY, scale });
  }, [windowStates, canvasTransform.scale, viewportWidth, viewportHeight, handleViewportChange]);

  // We'll reference the Navigator so we can call handleToggle() from outside
  const navigatorRef = useRef(null);

  // toggleNavigator calls handleToggle inside Navigator via ref
  const toggleNavigator = useCallback(() => {
    if (navigatorRef.current) {
      navigatorRef.current.handleToggle();
    }
  }, []);

  const hostApi = useMemo(() => ({
    setGalleryVisibility: setShowGallery,
    canvasTransform,
    // Navigate to internal routes (supports redirects like /contact, /about, /pages/slug)
    navigate: (path) => navigate(path),
    windowManager: {
      bringToFront: (windowId) => bringWindowToFront(windowId),
      minimize: (windowId) => toggleMinimize(windowId),
      close: (windowId) => closeWindowAndUpdateUrl(windowId),
      spawnWindow: (config) => spawnChildWindow(config),
      getSpawnedWindows: (parentId) => getChildWindows(parentId)
    }
  }), [canvasTransform, navigate, bringWindowToFront, toggleMinimize, closeWindowAndUpdateUrl, spawnChildWindow, getChildWindows]);

  // Track preview windows opened by SDK
  const previewWindowsRef = useRef(new Map());
  // Pending snap-to-window after preview opens
  const pendingSnapRef = useRef(null);

  // Extend window.parentApi with preview/window methods that the SDK expects
  useEffect(() => {
    if (!window.parentApi) return;

    window.parentApi.openPreview = (config) => {
      const { url, title = 'Preview', displayMode = 'windowed', serviceName } = config;
      if (!url) return Promise.reject(new Error('openPreview requires a url'));

      const windowId = `preview-${serviceName || 'sdk'}-${Date.now()}`;
      const previewApp = {
        id: windowId,
        name: title,
        type: 'internal',
        display_mode: displayMode === 'fullscreen' ? 'fullscreen' : 'windowed',
        component: IframePreview,
        metadata: { url, serviceName, isPreview: true }
      };

      wmLaunchApp(previewApp);
      previewWindowsRef.current.set(windowId, config);
      pendingSnapRef.current = windowId;
      return Promise.resolve(windowId);
    };

    window.parentApi.closePreview = (windowId) => {
      if (previewWindowsRef.current.has(windowId)) {
        closeWindowAndUpdateUrl(windowId);
        previewWindowsRef.current.delete(windowId);
        return Promise.resolve({ success: true });
      }
      return Promise.reject(new Error(`Preview window not found: ${windowId}`));
    };

    window.parentApi.getPreviewWindows = () => {
      const windows = Array.from(previewWindowsRef.current.entries()).map(([id, config]) => ({
        id,
        ...config
      }));
      return Promise.resolve(windows);
    };

    window.parentApi.setPreviewFullscreen = (windowId, fullscreen) => {
      // For now, just acknowledge - fullscreen toggle would require more integration
      return Promise.resolve({ success: true });
    };

    window.parentApi.getCurrentUser = window.parentApi.getUserContext;

    return () => {
      if (window.parentApi) {
        delete window.parentApi.openPreview;
        delete window.parentApi.closePreview;
        delete window.parentApi.getPreviewWindows;
        delete window.parentApi.setPreviewFullscreen;
        delete window.parentApi.getCurrentUser;
      }
    };
  }, [wmLaunchApp, closeWindowAndUpdateUrl]);

  // Snap to preview window once its state is initialized
  useEffect(() => {
    const targetId = pendingSnapRef.current;
    if (targetId && windowStates[targetId]) {
      pendingSnapRef.current = null;
      snapViewportToApp(targetId);
    }
  }, [windowStates, snapViewportToApp]);

  // A ref for the navigator button in the menu bar
  const navigatorButtonRef = useRef(null);

  return (
    <div className="flex flex-col h-dvh md:h-screen overflow-hidden">
      <div className="flex-none z-50 bg-gray-800 h-10 md:h-12 w-full">
        <MenuBar
          setShowGallery={setShowGallery}
          activeApps={activeApps} // Raw activeApps
          windowStates={windowStates}
          focusedWindowId={focusedWindowId}
          toggleMinimize={toggleMinimize}
          bringWindowToFront={bringWindowToFront}
          snapViewportToApp={snapViewportToApp}
          selectedAppIds={selectedAppIds}
          setSelectedAppIds={setSelectedAppIds}
          applyLayoutToSelectedApps={applyLayoutToSelectedApps}
          closeWindow={closeWindowAndUpdateUrl}
          toggleNavigator={toggleNavigator} // This calls navigatorRef.current.handleToggle()
          navigatorButtonRef={navigatorButtonRef}
          launchSDK={launchSDK}
        />
      </div>

      <Navigator
        ref={navigatorRef} // Pass ref to Navigator, so we can call handleToggle()
        activeApps={activeApps}
        windowStates={windowStates}
        focusedWindowId={focusedWindowId}
        canvasTransform={canvasTransform}
        onViewportChange={handleViewportChange}
        onVisibilityChange={() => {}} // If you originally had this prop
        topOffset={48}
        viewportWidth={viewportWidth}
        viewportHeight={viewportHeight}
        buttonRef={navigatorButtonRef}
      />

      <div className="flex-1 relative">
        <DraggableCanvas
          ref={canvasRef}
          canvasTransform={canvasTransform}
          onTransformChange={(transform) => {
            setCanvasTransform(prev => {
              if (
                prev.scale === transform.scale &&
                prev.translateX === transform.translateX &&
                prev.translateY === transform.translateY
              ) {
                return prev;
              }
              return transform;
            });
          }}
          disableNavigation={false} // Since Navigator toggles internally
          onDimensionsChange={(w, h) => {
            setViewportWidth(w);
            setViewportHeight(h);
          }}
        >
          {activeApps
            .map(getAppWithVersion)
            .filter(app => app.display_mode?.toLowerCase() === 'windowed')
            .map(app => {
              const windowState = windowStates[app.id] || {};
              if (windowState.isMinimized) return null;
              const posX = windowState.position?.x ?? 100;
              const posY = windowState.position?.y ?? 100;
              const w = windowState.size?.width ?? 800;
              const h = windowState.size?.height ?? 600;

              return (
                <DraggableWindow
                  key={app.id}
                  windowId={app.id}
                  title={app.name}
                  isResizable={true}
                  onFocus={() => bringWindowToFront(app.id)}
                  onClose={() => closeWindowAndUpdateUrl(app.id)}
                  onMinimize={() => toggleMinimize(app.id)}
                  position={{ x: posX, y: posY }}
                  size={{ width: w, height: h }}
                  zIndex={windowState.zIndex || 1}
                  onPositionChange={(pos) => updateWindowPosition(app.id, pos)}
                  onSizeChange={(sz) => updateWindowSize(app.id, sz)}
                  metadata={app.metadata}
                  applyLayout={(layoutName) => applyLayoutToSelectedApps(layoutName, [app.id])}
                >
                  {app.component ? React.createElement(app.component, {
                    hostApi,
                    windowId: app.id,
                    version: app.selectedVersion,
                    metadata: app.metadata
                  }) : null}
                </DraggableWindow>
              );
            })}
        </DraggableCanvas>
      </div>

      <div className="relative" style={{ zIndex: 99999 }}>
        {activeApps
          .map(getAppWithVersion)
          .filter(app => app.display_mode?.toLowerCase() === 'fullscreen')
          .map(app => {
            const windowState = windowStates[app.id] || {};
            if (windowState.isMinimized) return null;
            return (
              <FullscreenWindow
                key={app.id}
                title={app.name}
                onClose={() => closeWindowAndUpdateUrl(app.id)}
                zIndex={windowState.zIndex || 99999}
              >
                {app.component ? React.createElement(app.component, {
                  hostApi,
                  windowId: app.id,
                  version: app.selectedVersion,
                  metadata: app.metadata
                }) : null}
              </FullscreenWindow>
            );
          })}
      </div>

      <TabbedInterface
        isVisible={showGallery}
        onClose={() => {
          if (activeApps.length === 0 && window.location.pathname !== '/') {
            updateUrl(null);
          }
          setShowGallery(false);
        }}
        launchApp={launchApp}
        apps={apps.map(getAppWithVersion)}
        loading={loading}
        error={error}
        onVersionSwitch={handleVersionSwitch}
        initialTab={activeTab}
        launchSDK={launchSDK}
      />

      {/* Render modal components from redirects (e.g., ContactForm) */}
      {renderModal()}

      {/* Render page viewer from redirects */}
      {renderPageViewer()}
    </div>
  );
}

export default AppLauncher;
